import { useEffect, useRef, useState } from "react";
import axios from "axios";
import config from "../../../../config";

const API_URL = config.TRAFFIC;

// function formatTime(seconds) {
//   if (!seconds || seconds < 0) return ""; // Handle empty or invalid values
//   const minutes = Math.floor(seconds / 60);
//   const remainingSeconds = Math.round(seconds % 60);
//   return minutes > 0 ? `${minutes} min ${remainingSeconds} sec` : `${remainingSeconds} sec`;
// }

function formatTime(value) {
  if (value == null || isNaN(value)) return "";

  const isNegative = value < 0;
  const absValue = Math.abs(value);

  const [minutePart, secondPart] = absValue.toString().split(".");
  const totalMinutes = parseInt(minutePart, 10);
  const seconds = secondPart ? parseInt(secondPart.padEnd(2, "0"), 10) : 0;

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  let result = isNegative ? "-" : "";
  if (hours > 0) result += `${hours} hr `;
  if (minutes > 0 || hours > 0) result += `${minutes} min `;
  result += `${seconds} sec`;

  return result.trim();
}

function transformAPI(apiData) {
  return apiData
    .map((data) => [
      {
        id: data.id,
        route_id: data.id_route,
        titleID: data.id_route,
        title: data.name ?? `Route${data.id_route}`,
        metrics: [
          {
            name: "Average Route Speed",
            values: [
              {
                label: "Current",
                value: `${Math.round(data.avg_speed_current)} km/h`,
                status:
                  data.avg_speed_current > data.avg_speed_typical
                    ? "pass"
                    : "notPass",
              },
              {
                label: "Typical",
                value: `${Math.round(data.avg_speed_typical)} km/h`,
                status: "neutral",
              },
              {
                label: "Forecasted",
                value: data.avg_speed_forecasted
                  ? `${Math.round(data.avg_speed_forecasted)} km/h`
                  : "",
                status: "neutral",
              },
            ],
            description: `Shows the <strong>average speed</strong> in the monitored route. A lower speed than typical is colored in <strong>red</strong>, otherwise is colored in <strong>green</strong>. The <strong>Forecasted</strong> section indicates the predicted average route speed for the <strong>next 5 minutes</strong>.`,
          },
          {
            name: "Average Route Delay",
            values: [
              {
                label: "Delay",
                value: formatTime(data.delay),
                status: data.delay > 0 ? "pass" : "notPass",
              },
              {
                label: "Forecasted",
                value: data.delay_forecasted ? formatTime(data.delay_forecasted) : "",
                status: "neutral",
              },
            ],
            description: `Represents the <strong>average extra time (in minutes)</strong> drivers spend in traffic compared to <strong>typical flow conditions</strong> in the monitored route. Delays lower that the typical are colored in <strong>green</strong>, otherwise are colored in <strong>red</strong>. The <strong>Forecasted</strong> section indicates the predicted average extra travel time in the route for the <strong>next 5 minutes</strong>.`,
          },
          {
            name: "Average Route Travel Time / 10 km",
            values: [
              {
                label: "Current",
                value: formatTime(data.tt_current),
                status: data.tt_typical > data.tt_current ? "pass" : "notPass",
              },
              {
                label: "Typical",
                value: formatTime(data.tt_typical),
                status: "neutral",
              },
              {
                label: "Forecasted",
                value: data.tt_forecasted ? formatTime(data.tt_forecasted) : "",
                status: "neutral",
              },
            ],
            description: `<strong>Average time</strong> required to <strong>travel 10 km</strong> across the monitored route. A higher travel time than typical is colored in <strong>red</strong>, otherwise is colored in <strong>green</strong>. The <strong>Forecasted</strong> section indicates the predicted average time needed to travel 10 km in the <strong>next 5 minutes</strong>.`,
          },
          {
            name: "Traffic Congestion (%)",
            values: [
              {
                label: "Current",
                value: `${data.congestion_current} %`,
                status:
                  data.congestion_current <= data.congestion_typical
                    ? "pass"
                    : "notPass",
              },
              {
                label: "Typical",
                value: `${Math.round(data.congestion_typical)} %`,
                status: "neutral",
              },
              {
                label: "Forecasted",
                value: `${Math.round(data.congestion_forecasted)} %`,
                status: "neutral",
              },
            ],
            description: `Length-weighted <strong>percentage</strong> of included paths, where predicted speed is below <strong>60%</strong> of the free-flow speed.`,
          },
        ],
      },
    ])
    .flat();
}

const useRouteData = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [lastUpdated, setLastUpdated] = useState(null);

  const isFirstLoad = useRef(true);
  const intervalId = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const start = new Date();
        console.log(`[${start.toLocaleTimeString()}] Fetching route data…`);

        const response = await axios.get(
          `${API_URL}/latest_traffic_data_per_route`,
          {
            headers: { "Accept-Profile": "traffic_athens" },
          }
        );

        // console.log("response", response);
        const formatted = transformAPI(response.data);
        setData(formatted);

        // log the formatted data
        // console.log(
        //   `[${new Date().toLocaleTimeString()}] 📊 Received Route data:`,
        //   formatted
        // );

        const end = new Date();
        // console.log(
        //   `[${end.toLocaleTimeString()}] Data received (took ${(
        //     (end - start) /
        //     1000
        //   ).toFixed(2)}s)`
        // );

        setLastUpdated(end);
      } catch (err) {
        console.error("Error fetching traffic data:", err);
      } finally {
        if (isFirstLoad.current) {
          setLoading(false);
          isFirstLoad.current = false;
        }
      }
    };

    fetchData();

    intervalId.current = setInterval(fetchData, 60000);
    return () => {
      clearInterval(intervalId.current);
    };
  }, []);

  return { data, loading };
};

export default useRouteData;
